import React, { Fragment } from 'react';
import { Stack, Typography, Box, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useIsMobile } from '../../hooks';

const getLeftPosition = (isMobile, index) => {
  if (isMobile) return 'auto';
  return index % 2 === 0 ? '56%' : 'auto';
};

const getRightPosition = (isMobile, index) => {
  if (isMobile) return 'auto';
  return index % 2 === 1 ? '56%' : 'auto';
};

const getAlignment = (isMobile, index) => {
  if (isMobile) return 'flex-start';
  return index % 2 === 0 ? 'flex-start' : 'flex-end';
};

const getTextAlignment = (isMobile, index) => {
  if (isMobile) return 'left';
  return index % 2 === 0 ? 'left' : 'right';
};

const AlternatingStepper = ({ steps, iconSize }) => {
  const isMobile = useIsMobile('desktop');
  const theme = useTheme();
  const fontSize = `${parseInt(iconSize, 16) * 0.5}rem`;
  return (
    <Stack
      alignItems={isMobile ? 'flex-start' : 'center'}
      sx={{ position: 'relative' }}
      width="100%"
    >
      {steps.map((step, index) => (
        <Fragment key={step.id}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {/* Circle */}
            <Box
              sx={{
                width: iconSize,
                height: iconSize,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderColor: theme.palette.primary.A700,
                backgroundColor: theme.palette.common.white,
                fontWeight: 'bold',
                border: `3px solid ${theme.palette.primary.A700}`,
                zIndex: 1,
              }}
            >
              <Typography
                fontSize={{ xs: '1.2rem', sm: fontSize }}
                fontWeight={600}
                color={theme.palette.primary.A700}
                sx={{
                  borderRadius: '50%',
                  borderColor: theme.palette.primary.A700,
                }}
              >
                {Number(step.id) + 1}
              </Typography>
            </Box>
            {/* Text */}
            <Box
              sx={{
                position: isMobile ? 'relative' : 'absolute',
                left: getLeftPosition(isMobile, index),
                right: getRightPosition(isMobile, index),
                maxWidth: '45%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: getAlignment(isMobile, index),
                width: '100%',
              }}
            >
              <Typography
                fontSize={{ xs: '1rem', sm: '1.2rem' }}
                fontWeight={600}
                color="white"
                textAlign={getTextAlignment(isMobile, index)}
                sx={{ width: '100%', paddingLeft: isMobile ? '0.5rem' : 0 }}
              >
                {step.stepTitle}
              </Typography>
              {step.description && (
                <Typography
                  variant="small_2"
                  color="white"
                  fontWeight="bold"
                  textAlign={getTextAlignment(isMobile, index)}
                  sx={{ width: '100%' }}
                >
                  {step.description}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Line */}
          {index < steps.length - 1 && (
            <Stack
              sx={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                transform: isMobile ? 'translateX(-24%)' : 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 4,
                  minHeight: '3rem',
                  height: '100%',
                  bgcolor: theme.palette.primary.A700,
                  marginLeft: {
                    xs: 1,
                    smartphone: 1.2,
                    s: 1.5,
                    phone: 2,
                    sm: 2.5,
                    md: 3.6,
                    desktop: 0.8,
                    lg: 0.5,
                  },
                  position: 'relative',
                }}
              />
            </Stack>
          )}
        </Fragment>
      ))}
    </Stack>
  );
};

export default AlternatingStepper;

AlternatingStepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
  })).isRequired,
  iconSize: PropTypes.string.isRequired,
};
